/*
 * File: c:\Users\j.mazat\Desktop\Moby\MobyBuy\mobybuy_backoffice\src\Content\Offers\OfferRowUserSingle\OfferRowUser.ts
 * Project: c:\Users\j.mazat\Desktop\Moby\MobyBuy\mobybuy_backoffice
 * Created Date: Wednesday July 3rd 2024
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Wednesday July 3rd 2024 4:42:51 pm
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2024 Autobuy SAS
 * -----
 */

import { Button, TableCell, TableRow } from "@mui/material";
import RouterPathResolver from "Config/Router/RouterPathResolver";
import translator from "MultiLanguage/TranslationWrapper";
import { OfferStatus, OfferUserType } from "Type/offer.type";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Styles from "Content/Offers/OfferRowUserSingle/OfferRowUser.module.css";
import OfferService from "Services/Offer/OfferService";
import { FaDotCircle } from "react-icons/fa";
import { offerColor } from "Utils/offer.utils";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import LoadingScreen from "Content/LoadingScreen/LoadingScreen";

type OfferRowUserProps = {
    Offer: OfferUserType;
    showUser?: boolean
};

export function OfferRowUser(props: OfferRowUserProps) {
    const [myPrice, setMyPrice] = useState(props.Offer.Amount);
    const [myStatus, setMyStatus] = useState(props.Offer.Status);
    const [myComments, setMyComments] = useState(props.Offer.Comments);
    const [isSendable, setIsSendable] = useState(false);
    const statusOptions = ["ACCEPTED", "RESERVED", "CANCEL"];
    // const isDisabled = props.Offer.InSell.Status as unknown !== "SELLING" && !["ACCEPTED", "RESERVED"].includes(props.Offer.Status as unknown as string) ? true : false;
    const [isModifiable, setIsModifiable] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [additionnalText, setAdditionnalText] = useState<string | string[] | undefined>();

    useEffect(() => {
        if (myPrice !== props.Offer.Amount || myStatus !== props.Offer.Status || myComments !== props.Offer.Comments)
            setIsSendable(true);
        else
            setIsSendable(false);
    }, [myPrice, myStatus, myComments]);

    useEffect(() => {
        OfferService.isLastInSell(props.Offer.InSell.id).then((result) => {
            if (result.data.isLast) {
                if (props.Offer.InSell.Status as unknown as string !== "SELLING") {
                    if (["ACCEPTED", "RESERVED"].includes(props.Offer.Status as unknown as string))
                        setIsModifiable(true);
                    else
                        setIsModifiable(false);
                } else
                    setIsModifiable(true);
                // setIsModifiable(props.Offer.InSell.Status as unknown as string !== "SELLING" && ["ACCEPTED", "RESERVED"].includes(props.Offer.Status as unknown as string) ? true : false);
            }
        }).catch((error) => {
            console.log(error);
        });
    }, []);

    const handleResponse = (text: string | string[], isOkay: boolean = false) => {
        setIsOkay(isOkay);
        setOpenModal(true);
        setSpinner(false);
        setAdditionnalText(text);
    };

    const handleSave = () => {
        const data: {Amount?: number, Comments?: string, Status?: OfferStatus} = {};
        if (myPrice !== props.Offer.Amount)
            data.Amount = myPrice;
        if (myStatus !== props.Offer.Status)
            data.Status = myStatus;
        if (myComments !== props.Offer.Comments)
            data.Comments = myComments;
        setSpinner(true);
        setIsSendable(false);
        OfferService.updateSingleOffer(data, props.Offer.id).then(() => {
            handleResponse("", true);
            setIsSendable(false);
        }).catch((error)=> {
            console.log(error);
            handleResponse(["Une erreur est survenue:", error.message]);
        });
    };

    return (
        <>
            <TableRow hover={isModifiable} className={`${isModifiable ? "" : Styles.disabledRow}`} >
                {
                    props.showUser ?
                    <>
                        <TableCell align="center" className={`${Styles.cells}`}>
                            <Link to={`${RouterPathResolver.pathUser_BY_ID.path.replace(":id", props.Offer.User.id)}`}>
                                {`${props.Offer.User.FirstName} ${props.Offer.User.LastName}`}
                            </Link>
                        </TableCell>
                        <TableCell align="center" className={`${Styles.cells}`}>
                            {props.Offer.User.Commercial && <p className="m-0">{`${props.Offer.User.Commercial.FirstName} ${props.Offer.User.Commercial.LastName}`}</p>}
                        </TableCell>
                    </>
                    : null
                }
                <TableCell align="center" className={`${Styles.cells}`}>
                    <p className="m-0">{new Date(props.Offer.CreatedAt).toLocaleString()}</p>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <p className="m-0">{new Date(props.Offer.UpdatedAt).toLocaleString()}</p>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <Link to={`${RouterPathResolver.pathCar_BY_ID.path.replace(":id", props.Offer.InSell.Car.id)}`}>
                        {`${props.Offer.InSell.Car.Brand} ${props.Offer.InSell.Car.Model}`}
                    </Link>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <Link to={`${RouterPathResolver.pathCar_BY_ID.path.replace(":id", props.Offer.InSell.Car.id)}`}>
                        {`${props.Offer.InSell.Car.RegNb}`}
                    </Link>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <p className="m-0">{new Date(props.Offer.InSell.CreatedAt).toLocaleString()}</p>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <p className="m-0">{translator.decode(`Status.${props.Offer.InSell.Status}`)}</p>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <input className={`${Styles.inputNumber}`} type="number" disabled={!isModifiable} value={myPrice} min={1} onChange={(e) => setMyPrice(parseInt(e.target.value))} />
                </TableCell>
                <TableCell align="center" className={`${Styles.status}`}>
                    <FaDotCircle className="me-2" fill={`${offerColor(myStatus as unknown as string)}`}/>
                    <select disabled={!isModifiable} className={`${Styles.statusSelector}`} name="status" value={myStatus} onChange={(e) => setMyStatus(e.target.value as unknown as OfferStatus)}>
                        <option disabled value="PENDING">{translator.decode("Status.PENDING")}</option>
                        {statusOptions.map(val => {return <option value={val}>{translator.decode(`Status.${val}`)}</option>})}
                    </select>
                </TableCell>
                <TableCell align="center">
                    <textarea maxLength={2048} disabled={!isModifiable} className={`${Styles.comments}`} value={myComments} onChange={(e) => setMyComments(e.target.value)} />
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <Button disabled={!isSendable} onClick={handleSave} >{translator.decode("dynamic.save")}</Button>
                    {
                        spinner ?
                            <LoadingScreen width="2rem" height="2rem" borderWidth="5px" />
                        :null
                    }
                </TableCell>
            </TableRow>
            <ModalConfirmation show={openModal} onClose={() => setOpenModal(false)} closeModal={() => setOpenModal(false)} isOkay={isOkay} additionnalMessage={additionnalText}/>
        </>
    )
};
